/**
 * Ticking experiments to compare, on the experiments list and an agent's page.
 * The ticks live in the URL (`?select=a,b`), like every other filter, so they
 * survive a refresh and a shared link. Any experiment that is not running can be
 * ticked (to compare, or to delete), at most five, and — as the mockup has it —
 * from one agent. Compare needs every ticked one to be completed or stopped.
 */

import { isLive, sourceName } from "@/features/experiments/status";
import { ButtonLink, Button } from "@/components/ui/button";
import type { ExperimentSummary } from "@/features/experiments/types";
import { useUrlState } from "@/lib/use-url-state";
import { compareHref, isComparable, MAX_COMPARE, MIN_COMPARE, parseIds } from "./selection";

export interface CompareSelection {
  selected: Set<string>;
  canPick: (e: ExperimentSummary) => boolean;
  toggle: (id: string) => void;
}

export function useCompareSelection(experiments: ExperimentSummary[]) {
  const url = useUrlState();
  const ids = parseIds(url.get("select"));
  const byId = new Map(experiments.map((e) => [e.id, e]));
  const picked = ids
    .map((id) => byId.get(id))
    .filter((e): e is ExperimentSummary => !!e && !isLive(e.status));
  const agent = picked.find((p) => p.agent)?.agent?.id;
  const selection: CompareSelection = {
    selected: new Set(picked.map((e) => e.id)),
    canPick: (e) =>
      !isLive(e.status) &&
      (picked.some((p) => p.id === e.id) ||
        (picked.length < MAX_COMPARE && (!agent || !e.agent || e.agent.id === agent))),
    toggle: (id) => {
      const next = ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id];
      url.set({ select: next.join(",") }, { replace: true });
    },
  };
  return { selection, picked };
}

export function CompareButton({ picked }: { picked: ExperimentSummary[] }) {
  const n = picked.length;
  const comparable = picked.every(isComparable);
  const note =
    n === 0
      ? "Tick two or more finished experiments to compare"
      : !comparable
        ? `${n} selected · only completed or stopped experiments can be compared`
        : n === 1
          ? "1 selected"
          : `${n} selected · ${picked[0] ? sourceName(picked[0]) : ""}`;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted">{note}</span>
      {n >= MIN_COMPARE && comparable ? (
        <ButtonLink variant="primary" to={compareHref(picked.map((e) => e.id))}>
          Compare ({n})
        </ButtonLink>
      ) : (
        <Button disabled>Compare</Button>
      )}
    </div>
  );
}

export function SelectBox({
  experiment,
  selection,
}: {
  experiment: ExperimentSummary;
  selection: CompareSelection;
}) {
  return (
    <input
      type="checkbox"
      className="size-4 accent-ink"
      aria-label={`Select ${experiment.name}`}
      checked={selection.selected.has(experiment.id)}
      disabled={!selection.canPick(experiment)}
      title={reasonNotPickable(experiment, selection)}
      onChange={() => selection.toggle(experiment.id)}
    />
  );
}

export const SelectCell = (props: Parameters<typeof SelectBox>[0]) => (
  <td className="w-8 pr-0!">
    <SelectBox {...props} />
  </td>
);

/** Why a tick box is disabled, as its tooltip; nothing when it is enabled. */
function reasonNotPickable(e: ExperimentSummary, selection: CompareSelection): string | undefined {
  if (selection.canPick(e)) return undefined;
  if (isLive(e.status)) return "Running experiments cannot be compared or deleted. Stop it or wait.";
  return `Pick at most ${MAX_COMPARE} experiments, from one agent.`;
}
